/** biome-ignore-all lint/suspicious/noExplicitAny: Library any is useful and contained */
/**
 * Manages GraphQL subscriptions with automatic deduplication.
 *
 * Subscriptions with identical documents and variables share a single underlying
 * connection. When the last subscriber unsubscribes, the connection is cleaned up.
 */
export class GraphQLSubscriptionManager {
    _client;
    subscriptions = new Map();
    constructor(_client) {
        this._client = _client;
    }
    /**
     * Creates a subscription to a GraphQL document.
     *
     * Multiple calls with the same document and variables will share a single
     * underlying connection. Each call returns an unsubscribe function that should
     * be called when the subscription is no longer needed.
     *
     * @returns A function that unsubscribes from the subscription and cleans up
     * the underlying connection if no other subscribers remain.
     */
    subscribe = (options) => {
        if (!this.subscriptions.has(options.document))
            this.subscriptions.set(options.document, new Map());
        const subs = this.subscriptions.get(options.document);
        const subKey = varKey(options.variables ?? {});
        if (!subs.has(subKey)) {
            subs.set(subKey, new GraphQLSubscription(this._client, options.document, options.variables ?? {}, () => {
                subs.delete(subKey);
                if (subs.size === 0)
                    this.subscriptions.delete(options.document);
            }));
        }
        const sub = subs.get(subKey);
        const unsub = sub.subscribe(options, options.replay ?? false);
        return () => {
            unsub();
        };
    };
}
/**
 * Represents a single GraphQL subscription with multiple subscribers.
 *
 * Manages the lifecycle of the underlying WebSocket connection. The connection
 * starts when the first subscriber is added and stops 15ms after the last
 * subscriber is removed (to handle rapid re-subscriptions).
 */
class GraphQLSubscription {
    client;
    document;
    variables;
    onStop;
    _inner_sub = null;
    subscribers = new Set();
    latest;
    running = false;
    constructor(client, document, variables, onStop) {
        this.client = client;
        this.document = document;
        this.variables = variables;
        this.onStop = onStop;
    }
    /**
     * Adds a subscriber to this subscription.
     *
     * Starts the underlying connection if not already running. With `replay`, the
     * latest value the connection has already delivered is handed to the new subscriber.
     * Returns an unsubscribe function that removes this subscriber and stops
     * the connection after 15ms if no subscribers remain.
     */
    subscribe = (sink, replay = false) => {
        this.start();
        this.subscribers.add(sink);
        if (replay && this.latest !== undefined)
            sink.next?.(this.latest);
        return () => {
            this.subscribers.delete(sink);
            setTimeout(() => {
                if (this.subscribers.size === 0)
                    this.stop();
            }, 15);
        };
    };
    /** Returns the number of active subscribers. */
    count = () => {
        return this.subscribers.size;
    };
    /** Starts the underlying WebSocket subscription if not already running. */
    start = () => {
        if (this._inner_sub)
            return;
        this.running = true;
        this._inner_sub = this.client.subscribe(this.document, this.variables, {
            next: (x) => {
                this.latest = x;
                this.subscribers.forEach((s) => s.next?.(x));
            },
            error: (x) => this.subscribers.forEach((s) => s.error?.(x)),
            complete: () => this.subscribers.forEach((s) => s.complete?.()),
        });
    };
    /** Stops the underlying WebSocket subscription and cleans up resources. */
    stop = () => {
        if (!this._inner_sub)
            return;
        this.running = false;
        this.latest = undefined;
        this._inner_sub();
        this._inner_sub = null;
        this.onStop?.();
    };
}
/** Creates a stable string key from GraphQL variables for deduplication. */
const varKey = (variables) => JSON.stringify(variables);
//# sourceMappingURL=subscriptions.js.map